<template>
  <v-container class="party-manager">
    <v-card flat color="transparent" dark>
      <div class="d-flex justify-space-between align-center mb-4">
        <div>
          <v-card-title class="white--text text-h5 font-weight-bold">Event Manager</v-card-title>
          <v-card-subtitle class="white--text">Erstelle Events für den Slider.</v-card-subtitle>
        </div>
        <v-btn color="#00963f" class="mr-md-4 white--text pulse-animation" @click="createNewParty">
          <v-icon left>mdi-sparkles</v-icon>
          Neues Event
        </v-btn>
      </div>

      <v-row>
        <v-col v-for="item in parties" :key="item._id" cols="12" sm="6" md="4">
          <v-card dark @click="editParty(item)" class="item-card d-flex flex-column" min-height="350" hover>
            <v-img :src="'/img/' + item.image" height="250" class="white--text align-end gradient-overlay">
              <v-card-title class="font-weight-bold">{{ item.title }}</v-card-title>
            </v-img>
            <v-card-text class="pt-3">
              <div class="d-flex align-center mb-2">
                <v-icon small left color="#00963f">mdi-calendar</v-icon>
                <span class="ml-2 font-weight-bold">{{ $moment(item.eventDate).format("DD.MM.YYYY HH:mm") }}</span>
              </div>
              <div v-if="item.allowSignup" class="d-flex align-center">
                <v-chip small color="#00963f" outlined class="font-weight-bold">Anmeldung aktiv</v-chip>
                <span class="ml-2 caption grey--text text--lighten-2">
                  {{ item.signups ? item.signups.length : 0 }}
                  <span v-if="item.maxSignups > 0"> / {{ item.maxSignups }}</span>
                  Teilnehmer
                </span>
              </div>
              <div v-else class="d-flex align-center">
                <v-chip small color="grey" outlined>Keine Anmeldung</v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog v-model="showEditDialog" fullscreen transition="dialog-bottom-transition">
      <v-card dark>
        <v-toolbar dark color="#00963f">
          <v-btn icon dark @click="showEditDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Event bearbeiten</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn dark text @click="saveParty" :loading="saving">Speichern</v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-container>
          <v-row>
            <v-col cols="12" md="4">
              <v-card outlined class="pa-2 mb-4 image-upload-card" color="grey darken-4">
                <div class="subtitle-2 text-center mb-2 font-weight-bold">1. Slider Bild (Startseite)</div>
                <div class="caption text-center grey--text text--lighten-1 mb-2">Format: Querformat 3:1</div>
                <slim-cropper :options="slimOptionsLandscape" style="width: 100%">
                  <img v-if="party.image" :src="'/img/' + party.image" alt="Slider Image" />
                  <input type="file" accept="image/*" />
                </slim-cropper>
              </v-card>
            </v-col>

            <v-col cols="12" md="8">
              <v-text-field v-model="party.title" label="Event Titel" outlined class="text-h5"></v-text-field>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field type="datetime-local" label="Event Datum & Zeit" v-model="formattedEventDate" outlined></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="party.location" label="Ort / Studio" outlined prepend-inner-icon="mdi-map-marker"></v-text-field>
                </v-col>
              </v-row>

              <v-card outlined class="pa-4 mb-6 neon-border-card" color="grey darken-4">
                <div class="subtitle-1 font-weight-bold mb-2 primary--text">Anmeldung Einstellungen</div>
                <v-row align="center">
                  <v-col cols="12" sm="6">
                    <v-switch v-model="party.allowSignup" color="#00963f" label="Anmeldung erlauben" hide-details class="mt-0"></v-switch>
                  </v-col>
                  <v-col cols="12" sm="6" v-if="party.allowSignup">
                    <v-text-field
                      v-model.number="party.maxSignups"
                      type="number"
                      label="Max. Teilnehmer (0 = unendlich)"
                      hide-details
                      dense
                      outlined
                      hint="Bei 0 gibt es kein Limit"
                      persistent-hint
                      class="neon-input">
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-card>

              <v-card v-if="party.allowSignup && party._id" outlined class="pa-4 mb-6" color="grey darken-4">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="subtitle-1 font-weight-bold primary--text">Teilnehmerliste ({{ party.signups ? party.signups.length : 0 }})</div>
                  <v-btn icon small @click="loadData"><v-icon>mdi-refresh</v-icon></v-btn>
                </div>

                <v-data-table
                  :headers="signupHeaders"
                  :items="party.signups"
                  :items-per-page="5"
                  dark
                  dense
                  class="elevation-0 transparent-table"
                  no-data-text="Noch keine Anmeldungen vorhanden.">
                  <template v-slot:item.vorname="{ item }">
                    <span class="font-weight-medium">{{ item.vorname }}</span>
                  </template>
                  <template v-slot:item.nachname="{ item }">
                    <span class="font-weight-medium">{{ item.nachname }}</span>
                  </template>
                  <template v-slot:item.email="{ item }">
                    <span class="grey--text text--lighten-1">{{ item.email }}</span>
                  </template>
                </v-data-table>
              </v-card>

              <div class="subtitle-1 mb-1 font-weight-bold">Beschreibung</div>
              <vue-editor v-model="party.description" :editorToolbar="customToolbar"></vue-editor>

              <v-divider class="my-6"></v-divider>

              <div class="subtitle-1 mb-3 font-weight-bold">Sichtbarkeit</div>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-menu v-model="menuShow" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="party.showAfter"
                        label="Anzeigen ab"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined></v-text-field>
                    </template>
                    <v-date-picker v-model="party.showAfter" @input="menuShow = false" no-title scrollable locale="de"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-menu v-model="menuHide" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="party.hideAfter"
                        label="Ausblenden ab"
                        prepend-icon="mdi-calendar-remove"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined></v-text-field>
                    </template>
                    <v-date-picker v-model="party.hideAfter" @input="menuHide = false" no-title scrollable locale="de"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>

              <v-select
                v-model="party.studios"
                :items="studios"
                item-text="name"
                item-value="_id"
                label="Sichtbar in Studios"
                multiple
                chips
                outlined
                class="mt-2"></v-select>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "../../../api"
import Slim from "../../../plugins/slimImage/slim/slim.vue"
import { VueEditor } from "vue2-editor"
import moment from "moment"

export default {
  name: "communicationDashboardParty",
  components: { "slim-cropper": Slim, VueEditor },
  data() {
    return {
      parties: [],
      party: {},
      studios: [],
      showEditDialog: false,
      saving: false,

      menuShow: false,
      menuHide: false,

      // Header für die Teilnehmer-Tabelle
      signupHeaders: [
        { text: "Vorname", value: "vorname" },
        { text: "Nachname", value: "nachname" },
        { text: "E-Mail", value: "email" },
      ],

      customToolbar: [[{ header: [false, 1, 2, 3] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"]],

      slimOptionsLandscape: {
        ratio: "3:1",
        service: this.uploadPictureLandscape,
        serviceFormat: "file",
        didInit: (data, slim) => {
          this.slimInstanceL = slim
        },
        label: "Slider Bild hochladen",
        initialImage: null,
      },
      slimOptionsPortrait: {
        ratio: "9:16",
        service: this.uploadPicturePortrait,
        serviceFormat: "file",
        didInit: (data, slim) => {
          this.slimInstanceP = slim
        },
        label: "Popup Bild hochladen",
        initialImage: null,
      },
    }
  },
  computed: {
    formattedEventDate: {
      get() {
        if (!this.party.eventDate) return ""
        return moment(this.party.eventDate).format("YYYY-MM-DDTHH:mm")
      },
      set(val) {
        this.party.eventDate = val
      },
    },
  },
  async created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        this.studios = await api.fetchStudios()
        this.parties = await api.fetchAllParties()
        this.$emit("finishedloading")
      } catch (e) {
        this.$toast.error("Fehler beim Laden der Daten")
      }
    },
    createNewParty() {
      this.party = {
        title: "",
        description: "",
        image: "",
        imagePortrait: "",
        allowSignup: true,
        maxSignups: 0,
        studios: [],
        signups: [], // Initialisieren als leeres Array
        showAfter: moment().format("YYYY-MM-DD"),
        hideAfter: moment().add(1, "month").format("YYYY-MM-DD"),
        eventDate: null,
        location: "",
      }
      this.showEditDialog = true
    },
    editParty(item) {
      // Deep Copy, um das Original nicht direkt zu mutieren
      this.party = JSON.parse(JSON.stringify(item))
      if (this.party.showAfter) this.party.showAfter = moment(this.party.showAfter).format("YYYY-MM-DD")
      if (this.party.hideAfter) this.party.hideAfter = moment(this.party.hideAfter).format("YYYY-MM-DD")

      if (this.party.studios && this.party.studios.length > 0 && typeof this.party.studios[0] === "object") {
        this.party.studios = this.party.studios.map((s) => s._id)
      }

      // Sicherstellen, dass signups ein Array ist
      if (!this.party.signups) this.party.signups = []

      this.showEditDialog = true
    },
    async saveParty() {
      if (!this.party.title || !this.party.eventDate) {
        this.$toast.error("Bitte Titel und Datum angeben.")
        return
      }

      this.saving = true
      try {
        // Wir kopieren das Objekt, um die signups wieder in IDs umzuwandeln für den Save-Request
        let partyToSave = JSON.parse(JSON.stringify(this.party))

        // Wenn Teilnehmer-Objekte geladen wurden (durch populate), wandle sie zurück in IDs
        if (partyToSave.signups && partyToSave.signups.length > 0 && typeof partyToSave.signups[0] === "object") {
          partyToSave.signups = partyToSave.signups.map((u) => u._id)
        }

        await api.saveParty(partyToSave)
        this.showEditDialog = false
        this.loadData() // Neu laden, um populate wieder auszuführen
        this.$toast.success("Event gespeichert!")
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Speichern.")
      }
      this.saving = false
    },

    uploadPictureLandscape(formdata, progress, success, failure) {
      this.performUpload(formdata, success, (name) => {
        this.party.image = name
      })
    },
    uploadPicturePortrait(formdata, progress, success, failure) {
      this.performUpload(formdata, success, (name) => {
        this.party.imagePortrait = name
      })
    },
    performUpload(formdata, success, callback) {
      const formData = new FormData()
      formData.append("file", formdata[0])

      api
        .uploadPartyImage(formData)
        .then((data) => {
          if (data.status) {
            callback(data.name)
            success(data)
          } else {
            console.error("Upload failed", data)
            this.$toast.error("Upload fehlgeschlagen")
          }
        })
        .catch((err) => {
          console.error(err)
          this.$toast.error("Fehler beim Upload")
        })
    },
  },
}
</script>

<style scoped>
.party-manager {
  padding: 20px;
}

/* Item Karten im Dashboard */
.item-card {
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  border-radius: 12px !important;
  background: linear-gradient(145deg, #2a2a2a, #1e1e1e);
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.5), 0px -5px 15px rgba(255, 255, 255, 0.05);
  border: 2px solid transparent;
  position: relative;
  overflow: hidden;
}

.item-card:hover {
  transform: translateY(-8px) scale(1.02);
  border-color: #00963f;
  box-shadow: 0px 8px 25px rgba(0, 150, 63, 0.4), 0px -8px 25px rgba(0, 150, 63, 0.1);
}

.gradient-overlay {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0) 100%);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

/* Neuer Event Button */
.pulse-animation {
  animation: pulse-button 2s infinite;
  box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.7);
}

@keyframes pulse-button {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.7);
  }
  70% {
    transform: scale(1.02);
    box-shadow: 0 0 0 15px rgba(0, 150, 63, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0);
  }
}

/* Edit Dialog Spezifische Styles */
.image-upload-card {
  border-radius: 8px;
  background: #1a1a1a !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.neon-border-card {
  border: 1px solid rgba(0, 150, 63, 0.3) !important;
  box-shadow: 0 0 15px rgba(0, 150, 63, 0.2);
  transition: all 0.3s ease-in-out;
}
.neon-border-card:hover {
  box-shadow: 0 0 25px rgba(0, 150, 63, 0.4);
}

/* Override für Vue-Editor */
.vue-editor {
  background-color: #222 !important;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.ql-toolbar {
  background-color: #333 !important;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.ql-container {
  background-color: #222 !important;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  min-height: 150px;
}
.ql-toolbar button,
.ql-toolbar .ql-picker-label {
  color: white !important;
}

/* Transparente Tabelle */
.transparent-table {
  background-color: transparent !important;
}
.transparent-table >>> .v-data-table__wrapper {
  background-color: transparent !important;
}
.transparent-table >>> tr:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
